import { useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';

import axiosClient from '../api/axiosClient';

import { authEnabled } from './config';
import {
  ALL_GRANTABLE_PERMISSIONS,
  INVERSE_PERMISSIONS,
  LEGACY_SHIM,
  PermissionCode,
} from './permissionCatalog';

/**
 * Shape of the `/api/me` response. Backend owns the canonical shape —
 * keep this in lockstep with `server/src/routes/me.ts`. Extra fields
 * are tolerated; missing required fields surface as a 403 via
 * `noAppAccessReason`.
 */
interface MeResponse {
  user: {
    id: number;
    email: string;
    displayName?: string | null;
  };
  roles: string[];
  adminViewEnabled: boolean;
  contentEnabled?: boolean;
  contentAuthoringEnabled?: boolean;
  storyblokSpaceId?: string;
  /** RBAC_V2 env flag, always present (false when off or on a legacy server). */
  rbacV2Enabled: boolean;
  /** RBAC_V2 single role; null/absent ⇒ Default. Absent entirely when flag off. */
  role?: { code: string; name: string } | null;
  /** RBAC_V2 effective permission codes (the role's set). Absent when flag off. */
  permissions?: string[];
}

/**
 * Reason the current identity was denied app access, surfaced from the
 * `/api/me` 403 body. UI renders a clean "why" message instead of a
 * generic auth failure.
 */
export type AppAccessDenial = 'no_app_access' | 'email_not_verified' | null;

export interface CurrentUser {
  /** Backend user id (0 in the synthetic local-dev identity). */
  id: number;
  /** Display name, e.g. "Jane Doe" or email when name is absent. */
  name: string;
  /** Alias for `name` — kept for call-site back-compat. */
  displayName: string;
  /** Email from the token, or empty string when unavailable. */
  email: string;
  /** 1–2 character avatar initials. */
  initials: string;
  /** Role codes granted to this user. */
  roles: string[];
  /** Server-side `ADMIN_VIEW_ENABLED` kill-switch AND `/api/me` resolved. */
  adminViewEnabled: boolean;
  /** Convenience: is `'super-admin'` among roles. */
  isSuperAdmin: boolean;
  /** Generic role check. */
  hasRole: (role: string) => boolean;
  /** True while Auth0 OR `/api/me` is still resolving. */
  isLoading: boolean;
  /** True when Auth0 says the user is signed in AND `/api/me` succeeded. */
  isAuthenticated: boolean;
  /** Non-null when `/api/me` returned 403 with a known reason code. */
  noAppAccessReason: AppAccessDenial;
  /**
   * Storyblok space id from `/api/me` (for building Storyblok deep links).
   * Reactive — populated when the `/api/me` query resolves, so consumers
   * re-render once it's known rather than relying on the imperative
   * `window.__SUNNY_CONFIG__` seed (which races first paint). '' when unknown.
   */
  storyblokSpaceId: string;
  /** RBAC_V2 flag as served by `/api/me` (false in flag-off + legacy servers). */
  rbacV2Enabled: boolean;
  /** RBAC_V2 single role; null when flag off. */
  role: { code: string; name: string } | null;
  /** RBAC_V2 raw permission codes; [] when flag off. */
  permissions: string[];
  /**
   * Flag-aware permission check — the single shim. Flag on: super-admin
   * bypasses every code except the inverse ones (`hide_chrome`), which are
   * checked by literal membership only; otherwise checks `permissions`.
   * Flag off: reproduces today's exact gates via `LEGACY_SHIM`, so call
   * sites never need to branch on the flag themselves.
   */
  hasPermission: (code: PermissionCode) => boolean;
}

/**
 * Builds a flag-aware `hasPermission` closure. Shared by the local-dev
 * synthetic identity and the real `/api/me` path so the shim logic lives
 * in exactly one place.
 */
const makeHasPermission = (
  rbacV2Enabled: boolean,
  isSuperAdmin: boolean,
  hasRole: (role: string) => boolean,
  permissions: string[],
): ((code: PermissionCode) => boolean) => {
  const permissionSet = new Set(permissions);
  return (code: PermissionCode): boolean => {
    if (rbacV2Enabled) {
      if (INVERSE_PERMISSIONS.has(code)) return permissionSet.has(code);
      return isSuperAdmin || permissionSet.has(code);
    }
    return LEGACY_SHIM[code]({ isSuperAdmin, hasRole });
  };
};

/**
 * Compute 1–2 letter initials. Prefers first+last of a multi-word name;
 * falls back to the first letter of the email local part.
 */
const computeInitials = (name: string, email: string): string => {
  const source = (name || email.split('@')[0] || '').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  const picks = parts.length >= 2 ? [parts[0][0], parts[parts.length - 1][0]] : [source[0]];
  return picks.join('').toUpperCase().slice(0, 2) || '?';
};

/** `process.env.REACT_APP_RBAC_V2 === 'true'` — the local-dev default for the flag. */
const readDevRbacFlag = (): boolean => process.env.REACT_APP_RBAC_V2 === 'true';

/**
 * Synthetic local-dev identity — super-admin with admin view enabled so
 * offline development works without a backend. Full-permission super-admin
 * (minus `hide_chrome`/`insights_client`) so every v2 surface works offline
 * too, regardless of which way `rbacV2Enabled` resolves.
 */
const LOCAL_DEV_ME: MeResponse = {
  user: { id: 0, email: 'dev@local', displayName: 'Local Dev' },
  roles: ['default', 'super-admin'],
  adminViewEnabled: true,
  rbacV2Enabled: readDevRbacFlag(),
  role: { code: 'super-admin', name: 'Super Admin' },
  permissions: ALL_GRANTABLE_PERMISSIONS,
};

/**
 * e2e/manual-testing knob — ONLY honored when `!authEnabled` (never reached
 * on a real deployment, since local dev is the only place `authEnabled` is
 * false). A JSON partial of `MeResponse` in `localStorage`, merged over
 * `LOCAL_DEV_ME` so a developer/Playwright spec can simulate any role
 * without a backend. `user` is merged one level deep; every other key is a
 * shallow override.
 */
const readLocalDevMeOverride = (): Partial<MeResponse> | null => {
  if (authEnabled) return null;
  try {
    const raw = window.localStorage.getItem('LOCAL_DEV_ME_OVERRIDE');
    if (!raw) return null;
    return JSON.parse(raw) as Partial<MeResponse>;
  } catch (_) {
    return null;
  }
};

const resolveLocalDevMe = (): MeResponse => {
  const override = readLocalDevMeOverride();
  if (!override) return LOCAL_DEV_ME;
  return {
    ...LOCAL_DEV_ME,
    ...override,
    user: { ...LOCAL_DEV_ME.user, ...override.user },
  };
};

/**
 * Returns the current authenticated user plus role / admin-view metadata
 * sourced from `/api/me`.
 *
 * In `authEnabled === false` mode, returns a synthetic super-admin so
 * local dev doesn't need Auth0 or a backend.
 */
export function useCurrentUser(): CurrentUser {
  const { user, isLoading: auth0Loading, isAuthenticated: auth0IsAuthenticated } = useAuth0();

  const meQuery = useQuery<MeResponse, unknown>({
    queryKey: ['me'],
    queryFn: async () => {
      const res = await axiosClient.get<MeResponse>('/api/me');
      return res.data;
    },
    enabled: authEnabled && auth0IsAuthenticated,
    staleTime: 60_000,
    retry: (failureCount, err: any) => {
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
  });

  return useMemo<CurrentUser>(() => {
    if (!authEnabled) {
      const me = resolveLocalDevMe();
      const displayName = me.user.displayName || 'Local Dev';
      const hasRole = (role: string) => me.roles.includes(role);
      const rbacV2Enabled = !!me.rbacV2Enabled;
      const role = me.role ?? null;
      // Flag on: derive from the v2 role directly (authoritative, doesn't
      // depend on the backend's synthesized `roles[]`); flag off: legacy.
      const isSuperAdmin = rbacV2Enabled ? role?.code === 'super-admin' : me.roles.includes('super-admin');
      const permissions = me.permissions ?? [];
      return {
        id: me.user.id,
        name: displayName,
        displayName,
        email: me.user.email,
        initials: computeInitials(displayName, me.user.email),
        roles: me.roles,
        adminViewEnabled: me.adminViewEnabled,
        isSuperAdmin,
        hasRole,
        isLoading: false,
        isAuthenticated: false,
        noAppAccessReason: null,
        // Local-dev: useCurrentUser doesn't hit /api/me, so let the deep-link
        // helper fall back to window.__SUNNY_CONFIG__ (AppBootstrap seeds it).
        storyblokSpaceId: '',
        rbacV2Enabled,
        role,
        permissions,
        hasPermission: makeHasPermission(rbacV2Enabled, isSuperAdmin, hasRole, permissions),
      };
    }

    const rawName = user?.name || '';
    const email = user?.email || '';
    const displayName = meQuery.data?.user.displayName || rawName || email || 'Unknown';

    const errStatus = (meQuery.error as any)?.response?.status;
    const errCode = (meQuery.error as any)?.response?.data?.error as string | undefined;
    const noAppAccessReason: AppAccessDenial =
      errStatus === 403 && (errCode === 'no_app_access' || errCode === 'email_not_verified')
        ? errCode
        : null;

    const meRoles = meQuery.data?.roles ?? [];
    const adminViewEnabled = !!meQuery.data?.adminViewEnabled;
    const hasRole = (role: string) => meRoles.includes(role);
    const rbacV2Enabled = !!meQuery.data?.rbacV2Enabled;
    const role = meQuery.data?.role ?? null;
    // Flag on: derive from the v2 role directly (authoritative, doesn't
    // depend on the backend's synthesized `roles[]`); flag off: legacy.
    const isSuperAdmin = rbacV2Enabled ? role?.code === 'super-admin' : meRoles.includes('super-admin');
    const permissions = rbacV2Enabled ? (meQuery.data?.permissions ?? []) : [];

    return {
      id: meQuery.data?.user.id ?? 0,
      name: displayName,
      displayName,
      email,
      initials: computeInitials(displayName, email),
      roles: meRoles,
      adminViewEnabled,
      isSuperAdmin,
      hasRole,
      isLoading: !!auth0Loading || (auth0IsAuthenticated && meQuery.isLoading),
      isAuthenticated: !!auth0IsAuthenticated && meQuery.isSuccess && !noAppAccessReason,
      noAppAccessReason,
      storyblokSpaceId: meQuery.data?.storyblokSpaceId ?? '',
      rbacV2Enabled,
      role,
      permissions,
      hasPermission: makeHasPermission(rbacV2Enabled, isSuperAdmin, hasRole, permissions),
    };
  }, [
    user,
    auth0Loading,
    auth0IsAuthenticated,
    meQuery.data,
    meQuery.error,
    meQuery.isLoading,
    meQuery.isSuccess,
  ]);
}
